import {
  ChevronRight as NarrowerIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { queryKeys } from "../../hooks/queries/keys";
import { useCategoryTree } from "../../hooks/queries/useCategoryTree";
import {
  CategoryDetailParams,
  getCategory,
} from "../../services/productPlaneApi";
import { monospaceStyle } from "../../theme";
import { CategoryDetail, CategoryTreeRow } from "../../types/categoryDetail";
import { EntityRef } from "../../types/productPlane";
import { ChainCardType } from "../../types/valueChain";
import { ChainCardRef } from "./valueChainState";

interface ValueChainRootFieldProps {
  // The root drawn now; null for none
  root: ChainCardRef | null;
  // The name the read gives the root; null until it has arrived
  rootLabel?: string | null;
  // The root's id as the read gives it, on the curated list for a kind;
  // shown at the end of the field
  rootId?: string | null;
  // Called with the named root when one is picked or submitted
  onSubmit: (root: ChainCardRef) => void;
}

// The prefix of a class's row id: a class found in articles, with no
// curated row of its own
const CLASS_ID = /^cat__/i;

// What an id pasted whole opens as: a class row's id as a class, any other
// id (a kind's id on the curated list, or the id of its row) as a kind
export const pastedType = (id: string): ChainCardType =>
  CLASS_ID.test(id) ? "class" : "kind";

// The rank of a curated row that is a kind
const KIND_RANK = "type";

// The smallest read of a category that still names its rank: one entry of
// each list
const RANK_LOOKUP: CategoryDetailParams = {
  products_limit: 1,
  narrower_limit: 1,
  capabilities_limit: 1,
  specifications_limit: 1,
  sources_limit: 1,
};

// Whether a draft is an id pasted whole rather than a name: a backbone id
// (`plt:frigate`, `backbone:category:eqp:gyroscope`) or a row id
// (`cat__<uuid>`)
export const isPastedId = (draft: string): boolean =>
  draft.includes(":") || /^[a-z]+__[0-9a-z-]+$/i.test(draft);

// What a row of the tree opens as: a curated row of type rank as a kind, any
// other row as a class
export const rootTypeOf = (
  category: Pick<CategoryDetail, "curated" | "rank">,
): ChainCardType =>
  category.curated && category.rank === KIND_RANK ? "kind" : "class";

// The rows of a level whose name holds the typed text, in any case
export const matchingRows = (
  rows: readonly CategoryTreeRow[],
  text: string,
): CategoryTreeRow[] => {
  const wanted = text.trim().toLowerCase();
  return wanted
    ? rows.filter((row) =>
        (row.category.name ?? row.category.id).toLowerCase().includes(wanted),
      )
    : [...rows];
};

const rowName = (ref: EntityRef): string => ref.name ?? ref.id;

// The field that names the root: a kind or a class found by name on the
// category tree, walked one level at a time, or an id pasted whole (a kind
// by its id on the curated list, `plt:frigate`, or by the id of its row; a
// class by its id). Typing a name filters the level shown; picking a row,
// pressing Enter or the search icon draws the chain. ⌘K (Ctrl K) focuses it.
const ValueChainRootField: React.FC<ValueChainRootFieldProps> = ({
  root,
  rootLabel = null,
  rootId = null,
  onSubmit,
}) => {
  const queryClient = useQueryClient();
  const shownName = rootLabel ?? root?.id ?? "";
  const [draft, setDraft] = useState(shownName);
  // Whether the draft is what the analyst typed, not the root's name
  const [typed, setTyped] = useState(false);
  const [open, setOpen] = useState(false);
  // The classes walked down to, the top of the tree first; empty at the top
  const [trail, setTrail] = useState<EntityRef[]>([]);
  const [picking, setPicking] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // The root changes without the field when the address does: the back
  // button, an opened link; its name arrives with the read
  useEffect(() => {
    setDraft(shownName);
    setTyped(false);
  }, [root?.id, root?.type, shownName]);

  // ⌘K, or Ctrl K, puts the cursor in the field from anywhere on the page
  useEffect(() => {
    const focus = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener("keydown", focus);
    return () => window.removeEventListener("keydown", focus);
  }, []);

  const level = trail.length > 0 ? trail[trail.length - 1].id : null;
  const tree = useCategoryTree(level, open);
  const pasted = typed && isPastedId(draft.trim());
  const rows = useMemo(
    () =>
      matchingRows(tree.data?.classes.items ?? [], typed && !pasted ? draft : ""),
    [tree.data, typed, pasted, draft],
  );

  const pick = async (row: CategoryTreeRow) => {
    setPicking(true);
    let pickedType: ChainCardType = "class";
    try {
      const category = await queryClient.fetchQuery({
        queryKey: queryKeys.productPlane.category(row.category.id, {
          ...RANK_LOOKUP,
        }),
        queryFn: () => getCategory(row.category.id, RANK_LOOKUP),
      });
      pickedType = rootTypeOf(category);
    } catch {
      // A row whose rank cannot be read opens as a class, which every row
      // of the tree can
    }
    setPicking(false);
    setOpen(false);
    setTyped(false);
    setDraft(rowName(row.category));
    onSubmit({ type: pickedType, id: row.category.id });
  };

  const submit = () => {
    const text = draft.trim();
    if (!text) {
      return;
    }
    if (isPastedId(text)) {
      setOpen(false);
      onSubmit({ type: pastedType(text), id: text });
      return;
    }
    if (rows.length > 0) {
      void pick(rows[0]);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        component="form"
        role="search"
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          submit();
        }}
        sx={{ flex: "0 1 380px", minWidth: 260, position: "relative" }}
      >
        <TextField
          fullWidth
          size="small"
          inputRef={input}
          value={draft}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setDraft(event.target.value);
            setTyped(true);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setOpen(false);
            }
          }}
          placeholder="Find a kind or a class by name, or paste an id such as plt:frigate"
          inputProps={{ "aria-label": "Root", maxLength: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {picking ? (
                  <CircularProgress size={16} aria-label="Opening the root" />
                ) : (
                  <IconButton
                    type="submit"
                    edge="start"
                    size="small"
                    aria-label="Draw the chain"
                    sx={{ color: "text.secondary" }}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 1 }}>
                {rootId && !typed && (
                  <Typography
                    component="span"
                    aria-label="Root id"
                    noWrap
                    sx={{
                      ...monospaceStyle,
                      fontSize: 12,
                      color: "text.disabled",
                      maxWidth: 140,
                    }}
                  >
                    {rootId}
                  </Typography>
                )}
                <Box
                  component="kbd"
                  aria-hidden
                  sx={{
                    ...monospaceStyle,
                    fontSize: 11,
                    color: "text.secondary",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderRadius: "4px",
                    px: "6px",
                    py: "1px",
                  }}
                >
                  ⌘K
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { backgroundColor: "background.paper" },
          }}
        />

        {open && (
          <Paper
            role="dialog"
            aria-label="Category tree"
            elevation={8}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: (theme) => theme.zIndex.modal,
              maxHeight: 360,
              overflow: "auto",
              p: 1,
              borderRadius: (theme) => `${theme.radius.surface}px`,
            }}
          >
            <Breadcrumbs aria-label="Tree level" sx={{ px: 1, pb: 0.5 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => setTrail([])}
              >
                All classes
              </Link>
              {trail.map((step, index) => (
                <Link
                  key={step.id}
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => setTrail(trail.slice(0, index + 1))}
                >
                  {rowName(step)}
                </Link>
              ))}
            </Breadcrumbs>

            {pasted ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 1, py: 0.5 }}
              >
                Press Enter to draw the chain of the{" "}
                {pastedType(draft.trim())} with this id.
              </Typography>
            ) : tree.isPending ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} aria-label="Reading the tree" />
              </Box>
            ) : tree.isError ? (
              <Typography
                variant="body2"
                color="error"
                sx={{ px: 1, py: 0.5 }}
              >
                The category tree could not be read; paste an id instead.
              </Typography>
            ) : rows.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 1, py: 0.5 }}
              >
                No class on this level matches.
              </Typography>
            ) : (
              <List dense aria-label="Classes" disablePadding>
                {rows.map((row) => {
                  const name = rowName(row.category);
                  return (
                    <ListItem
                      key={row.category.id}
                      disablePadding
                      secondaryAction={
                        row.has_narrower ? (
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label={`Narrower classes of ${name}`}
                            onClick={() => {
                              setTrail([...trail, row.category]);
                              setTyped(false);
                            }}
                          >
                            <NarrowerIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    >
                      <ListItemButton
                        aria-label={`Draw the chain of ${name}`}
                        disabled={picking}
                        onClick={() => void pick(row)}
                      >
                        <ListItemText primary={name} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default ValueChainRootField;
